"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";
import LockIcon from "@mui/icons-material/Lock";

import { useStyles } from "./styles";
import { Nullable } from "@/types/common";
import { Image as TImage } from "@/types/thread";
import { useAppSelector } from "@/redux/store";
import useSafetyFilter from "@/hooks/useSafetyFilter";
import Image from "@/components/common/Image";
import RatingControls from "@/components/social/ratings/RatingControls";
import CopyImage from "@/components/common/CopyImage";
import ImageViewer from "@/components/common/ImageViewer";
import InpaintButton from "../InpaintButton";
import DownloadButton from "@/components/common/DownloadButton";
import CarouselImage from "./CarouselImage";
import SafeSearchIndicator from "@/components/common/SafeSearchIndicator";
import GenCardPaywall from "@/components/common/PaywallsMicrotransactions/PaywallCTA/GenCardPaywall/PaywallA";

interface HighlightedImageProps {
  images: TImage[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
}

const HighlightedImage: React.FC<HighlightedImageProps> = ({
  images,
  selectedIndex,
  setSelectedIndex,
}) => {
  const { classes } = useStyles();
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down("md")
  );
  const router = useRouter();
  const pathname = usePathname() as string;
  const [selectedFullscreenIndex, setSelectedFullscreenIndex] = useState<
    number | null
  >(null);
  const [imageDimensions, setImageDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [touchStart, setTouchStart] = useState<Nullable<number>>(null);
  const [touchEnd, setTouchEnd] = useState<Nullable<number>>(null);
  const [showPaywall, setShowPaywall] = useState<boolean>(false);
  const blurHighlightedImage = useSafetyFilter(
    images[selectedIndex].safetyRating === 1
  );
  const user = useAppSelector((state) => state.user.user);
  const isSubscribed = Boolean(user && user.subscriptionType !== "free");

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
    router.push(`${pathname}?highlighted=${images[index].id}`);
  };

  const handleNext = () => {
    if (selectedIndex === images.length - 1) {
      setSelectedIndex(0);
    } else {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (selectedIndex === 0) {
      setSelectedIndex(images.length - 1);
    } else {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };

  const handleCloseImageViewer = () => {
    setSelectedFullscreenIndex(null);
  };

  const handleOpenFullscreenViewer = (index: number) => {
    if (blurHighlightedImage) {
      setShowPaywall(true);
      return;
    }
    setSelectedFullscreenIndex(index);
  };

  const handlePlaceholderClick = () => {
    setShowPaywall(true);
  };

  const handleUpgrade = () => {
    setShowPaywall(false);
    router.push("/subscribe");
  };

  useEffect(() => {
    // Reset loaded state when image changes
    setImageLoaded(false);

    const calculateImageDimensions = () => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;

      const img = new window.Image();
      img.onload = () => {
        const imageAspectRatio = img.naturalWidth / img.naturalHeight;
        const containerAspectRatio = containerWidth / containerHeight;

        let actualWidth, actualHeight;

        if (imageAspectRatio > containerAspectRatio) {
          // Image is wider than container
          actualWidth = containerWidth;
          actualHeight = containerWidth / imageAspectRatio;
        } else {
          // Image is taller than container
          actualHeight = containerHeight;
          actualWidth = containerHeight * imageAspectRatio;
        }

        setImageDimensions({ width: actualWidth, height: actualHeight });
        setImageLoaded(true); // Set loaded after dimensions are calculated
      };
      img.src = images[selectedIndex]?.url;
    };

    calculateImageDimensions();

    // Only add resize listener after initial load
    const handleResize = () => {
      if (imageLoaded) {
        calculateImageDimensions();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [selectedIndex, images]);

  if (!images[selectedIndex]) return null;

  return (
    <div className={classes.container}>
      <div
        ref={containerRef}
        className={classes.highlightedImageContainer}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={classes.imageWrapper}
          style={{
            width: imageDimensions.width || "100%",
            height: imageDimensions.height || "100%",
            position: "relative",
            margin: "auto",
          }}
        >
          <Image
            src={images[selectedIndex]?.url}
            alt={images[selectedIndex]?.alt || ""}
            className={classes.highlightedImage}
            onClick={() => handleOpenFullscreenViewer(selectedIndex)}
            style={{
              filter: blurHighlightedImage ? "blur(15px)" : "none",
            }}
            fill
            sizes="(max-width: 768px) 350px, 500px"
            priority
          />
          {imageLoaded && (
            <div className={classes.controls}>
              {!blurHighlightedImage && (
                <InpaintButton url={images[selectedIndex]?.url} color="white" />
              )}
              {!blurHighlightedImage && (
                <DownloadButton
                  url={images[selectedIndex]?.url}
                  color="white"
                />
              )}
              {!blurHighlightedImage && (
                <CopyImage
                  imageUrl={images[selectedIndex]?.url}
                  color="white"
                />
              )}
              <RatingControls
                imageId={images[selectedIndex].id}
                likesCount={images[selectedIndex].likesCount || 0}
                userRating={images[selectedIndex].userRating || null}
                key={images[selectedIndex].id}
              />
            </div>
          )}
        </div>
        {blurHighlightedImage && imageLoaded && <SafeSearchIndicator />}
      </div>
      {images.length > 1 && (
        <div className={classes.carousel}>
          {images.map((image, index) => {
            const shouldLockImage =
              !isSubscribed &&
              ((images.length === 4 && index >= 2) ||
                (images.length !== 4 && image.locked));

            if (shouldLockImage)
              return (
                <div
                  className={classes.lockedImageWrapper}
                  key={`highlighted-image-${index}`}
                  onClick={handlePlaceholderClick}
                >
                  <Image
                    src={image?.url}
                    alt={image?.alt || ""}
                    width={isMobile ? 80 : 120}
                    height={isMobile ? 80 : 120}
                    style={{
                      objectFit: "contain",
                      borderRadius: "4px",
                      filter: "blur(1rem)",
                      WebkitFilter: "blur(1rem)",
                    }}
                    className={classes.blurredImage}
                  />
                  <div className={classes.lockIconWrapper}>
                    <LockIcon className={classes.lockIcon} />
                  </div>
                </div>
              );
            return (
              <CarouselImage
                image={image}
                isSelected={index === selectedIndex}
                index={index}
                onSelect={handleSelect}
                key={`highlighted-image-${index}`}
              />
            );
          })}
        </div>
      )}
      {selectedFullscreenIndex !== null && (
        <ImageViewer
          initialSelection={selectedFullscreenIndex}
          images={
            !user || user?.subscriptionType === "free"
              ? images.length === 4
                ? images.slice(0, 2)
                : images
              : images
          }
          onClose={handleCloseImageViewer}
          showControlsOnlyAfterLoad={true}
        />
      )}
      {showPaywall && (
        <GenCardPaywall
          onClose={() => setShowPaywall(false)}
          onUpgrade={handleUpgrade}
          versionIdentifier="A"
          ctaContent={{
            modalTitle: "Unlock Premium Content",
            modalSubtitle: "Upgrade today to view all images in full HD!",
            feature1Title: "Full Gallery Access",
            feature1Desc: "View all images without restrictions",
            feature2Title: "HD Resolution",
            feature2Desc: "Enjoy images in maximum quality",
            feature3Title: "Private Creations",
            feature3Desc: "Hide your images from the public gallery",
          }}
        />
      )}
    </div>
  );
};

export default HighlightedImage;
